import { sendLogEvent } from './actions';
import { IS_EMITTING_LOG_EVENTS_DISABLED, IS_PRODUCTION_ENVIRONMENT } from '../../constants';
import * as C from './constants';
import { IShow, ITrack } from './Player.types';
import { stripHtml } from 'string-strip-html';

export const VALIDATION_ERROR = 'VALIDATION_ERROR';
export const USER_NOT_LOGGED_IN_ERROR = 'USER_NOT_LOGGED_IN_ERROR';

export const BAD_REQUEST_400 = 'BAD_REQUEST_400';
export const UNAUTHORIZED_401 = 'UNAUTHORIZED_401';
export const PAYMENT_REQUIRED_402 = 'PAYMENT_REQUIRED_402';
export const FORBIDDEN_403 = 'FORBIDDEN_403';
export const NOT_FOUND_ERROR_404 = 'NOT_FOUND_ERROR_404';
export const METHOD_NOT_ALLOWED_405 = 'METHOD_NOT_ALLOWED_405';
export const NOT_ACCEPTABLE_406 = 'NOT_ACCEPTABLE_406';
export const PROXY_AUTH_REQUIRED_407 = 'PROXY_AUTH_REQUIRED_407';
export const REQUEST_TIMEOUT_408 = 'REQUEST_TIMEOUT_408';
export const CONFLICT_409 = 'CONFLICT_409';
export const GONE_410 = 'GONE_410';
export const PAYLOAD_TOO_LARGE_413 = 'PAYLOAD_TOO_LARGE_413';
export const URI_TOO_LONG_414 = 'URI_TOO_LONG_414';
export const UNSUPPORTED_MEDIA_TYPE_415 = 'UNSUPPORTED_MEDIA_TYPE_415';
export const TOO_MANY_REQUESTS_429 = 'TOO_MANY_REQUESTS_429';
export const REQUEST_HEADER_FIELDS_TOO_LARGE_430 = 'REQUEST_HEADER_FIELDS_TOO_LARGE_430';
export const INTERNAL_SERVER_ERROR_500 = 'INTERNAL_SERVER_ERROR_500';
export const NOT_IMPLEMENTED_501 = 'NOT_IMPLEMENTED_501';
export const BAD_GATEWAY_502 = 'BAD_GATEWAY_502';
export const SERVICE_UNAVAILABLE_503 = 'SERVICE_UNAVAILABLE_503';
export const GATEWAY_TIMEOUT_504 = 'GATEWAY_TIMEOUT_504';

class PlayerService {
  base = process.env.REACT_APP_YR_API_ENDPOINT || 'https://api.youradio.cz/news/v2.0';

  getShowDetail = async ({
    showId,
    limit,
  }: {
    showId: string | undefined;
    limit: number;
  }): Promise<IShow> => {
    if (!showId) {
      throw new Error('VALIDATION_ERROR');
    }

    const res = await fetch(
      `${this.base}/shows/${encodeURI(showId)}?tracks=${encodeURI(limit.toString())}`,
    );

    if (res.status >= 200 && res.status < 300) {
      const showDetail: IShow = await res.json();

      try {
        if (showDetail.subtitle) {
          return {
            ...showDetail,
            subtitle: stripHtml(showDetail.subtitle).result,
          };
        }
      } catch (err) {
        // pass
      }

      return showDetail;
    }

    const { error } = await res.json();
    throw new Error(error || 'Neznámá chyba');
  };

  getTrackInfo = async (trackId: string): Promise<ITrack> => {
    if (!trackId) {
      throw new Error('VALIDATION_ERROR');
    }

    const res = await fetch(`${this.base}/tracks/${encodeURI(trackId)}`);

    await handleErrorResponse(res);

    return res.json();
  };

  sendLogEvent = async (options: ReturnType<typeof sendLogEvent>['payload']) => {
    const { event, trackId, trackTime } = options;

    if (IS_EMITTING_LOG_EVENTS_DISABLED && !IS_PRODUCTION_ENVIRONMENT) {
      console.warn(
        `PlayerService.sendLogEvent() - skipping sending event ${event} for song ${trackId}`,
        options,
      );
      return;
    }

    if (!trackId || !event || typeof trackTime !== 'number') {
      console.warn(
        `PlayerService.sendLogEvent() - skipping sending event ${event} for song ${trackId} due to missing params`,
        options,
      );
      return;
    }

    if ((event === C.LOG_EVENT_SKIP || event === C.LOG_EVENT_FINISHED) && trackTime === 0) {
      console.warn(
        `PlayerService.sendLogEvent() - skipping sending event ${event} for song ${trackId} due to trackTime length`,
        options,
      );
      return;
    }

    const res = await fetch(`${this.base}/logs`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        type: event,
        track_id: trackId,
        track_time: trackTime,
        timestamp: Math.floor(Date.now() / 1000),
        ...(options.trackFinalTime ? { track_final_time: options.trackFinalTime } : null),
      }),
    });

    if (res.status > 299) {
      console.error('PlayerService.sendEvent() - status', res.status);
      // intentionally not handling error (sending events is fire-and-forget action)
    }

    // intentionally not returning anything (sending events is fire-and-forget action)
  };
}

export async function handleErrorResponse(response: { status: number; json: () => Promise<any> }) {
  if (response.status >= 200 && response.status <= 300) {
    return null;
  }

  switch (response.status) {
    case 400: {
      throw new Error(BAD_REQUEST_400);
    }
    case 401: {
      throw new Error(UNAUTHORIZED_401);
    }
    case 402: {
      throw new Error(PAYMENT_REQUIRED_402);
    }
    case 403: {
      throw new Error(FORBIDDEN_403);
    }
    case 404: {
      throw new Error(NOT_FOUND_ERROR_404);
    }
    case 405: {
      throw new Error(METHOD_NOT_ALLOWED_405);
    }
    case 406: {
      throw new Error(NOT_ACCEPTABLE_406);
    }
    case 407: {
      throw new Error(PROXY_AUTH_REQUIRED_407);
    }
    case 408: {
      throw new Error(REQUEST_TIMEOUT_408);
    }
    case 409: {
      throw new Error(CONFLICT_409);
    }
    case 410: {
      throw new Error(GONE_410);
    }
    case 413: {
      throw new Error(PAYLOAD_TOO_LARGE_413);
    }
    case 414: {
      throw new Error(URI_TOO_LONG_414);
    }
    case 415: {
      throw new Error(UNSUPPORTED_MEDIA_TYPE_415);
    }
    case 429: {
      throw new Error(TOO_MANY_REQUESTS_429);
    }
    case 430: {
      throw new Error(REQUEST_HEADER_FIELDS_TOO_LARGE_430);
    }
    case 500: {
      throw new Error(INTERNAL_SERVER_ERROR_500);
    }
    case 501: {
      throw new Error(NOT_IMPLEMENTED_501);
    }
    case 502: {
      throw new Error(BAD_GATEWAY_502);
    }
    case 503: {
      throw new Error(SERVICE_UNAVAILABLE_503);
    }
    case 504: {
      throw new Error(GATEWAY_TIMEOUT_504);
    }
    default: {
      console.error(`handleErrorResponse() - status code: ${response.status}`, response);
      throw new Error('UNKNOWN_ERROR');
    }
  }
}

export default new PlayerService();
